import { TCart, TCartItemProduct, TDiscount, TProduct } from "../entities";
import { round2 } from "./money";

export * from "./money";
// import { DiscountEngine } from "../entities/Discount/engine";

const CONFIG = {
	VAT: 18,
};

/**
 * VAT-inclusive UNIT price for a priced line.
 *  - VAT-exempt product (`vat === false`): the price as-is, either way.
 *  - VAT-included store: the price already includes VAT.
 *  - VAT-excluded store: add VAT on top.
 */
export function unitPriceInclVat(
	finalPrice: number,
	hasVat: boolean,
	isVatIncludedInPrice: boolean,
): number {
	if (!hasVat) return finalPrice;
	return isVatIncludedInPrice ? finalPrice : finalPrice * (1 + CONFIG.VAT / 100);
}

/**
 * THE canonical money number: the VAT-inclusive charge for ONE cart line,
 * computed **exactly the way HYP charges it** — round the unit price to 2dp
 * first (that is the price string HYP receives in each heshDesc line), then
 * multiply by amount, then round the line: `round2(amount × round2(unitInclVat))`.
 *
 * Every system that charges or prints this cart derives its line total from
 * here — the cart display (`getCartCost().finalCost`), the HYP charge
 * (`buildFulfilledHeshDescItems`), and the fiscal document — so the three can
 * never diverge.
 *
 * Why round the unit FIRST: HYP's payment page is sent one line per product,
 * `[sku~name~amount~unitPrice2dp]`, and charges Σ amount × unitPrice2dp, then
 * validates it equals the Amount we send. HYP can only carry a 2dp unit price,
 * so that rounding is unavoidable the moment we send it the real products (which
 * we must, so the products appear on its receipt). Matching HYP's arithmetic
 * here — instead of rounding once after the multiply — is what removes the drift
 * that made the store under-charge vs its own invoice and forced the ≤0.20
 * "just let the transaction through" tolerance.
 */
export function chargeLineInclVat(
	finalPrice: number,
	hasVat: boolean,
	amount: number,
	isVatIncludedInPrice: boolean,
): number {
	const unitIncl = round2(unitPriceInclVat(finalPrice, hasVat, isVatIncludedInPrice));
	return round2(unitIncl * amount);
}

/**
 * Internal normaliser for getCartCost — not a pricer.
 *
 * Rewrite the raw items array so it only contains lines that should be charged:
 *  - "missing"     → dropped
 *  - "substituted" (with a truthy substitutedWith) → product and amount replaced
 *    with the substitute's. The substitute product's own discount is neutralised
 *    (discount: { type:"none", value:0 }) so getCartCost's getPriceAfterDiscount
 *    returns sub.price unchanged, keeping all callers (cartTotal, EZcount doc,
 *    delivery-note PDF, HYP charge) consistent.
 *  - everything else → passed through unchanged
 *
 * Pricing (finalPrice, VAT, discounts) is owned entirely by getCartCost — callers
 * that need priced line items must use getCartCost(...).items, not this function.
 *
 * Pure and idempotent: a second pass re-reads substitutedWith intact and
 * re-pins to the same values.
 */
export function getFulfilledCartItems(items: TCart["items"]): TCart["items"] {
	return items.reduce<TCart["items"]>((acc, item) => {
		if (item.status === "missing") {
			return acc;
		}
		if (item.status === "substituted" && item.substitutedWith) {
			const sub = item.substitutedWith;
			acc.push({
				...item,
				// Pin price to sub.price and neutralise the substitute product's own
				// discount so getPriceAfterDiscount returns sub.price with no reduction.
				product: { ...sub.product, price: sub.price, discount: { type: "none", value: 0 } },
				amount: sub.amount,
			} as TCartItemProduct);
			return acc;
		}
		acc.push(item);
		return acc;
	}, []);
}

function calculateDiscount(product: TProduct, isVatIncludedInPrice: boolean) {
	if (product.discount?.type === "percent") {
		return (product.price * (product.discount.value ?? 100)) / 100;
	}
	if (product.discount?.type === "number") {
		return product.discount.value ?? 0;
	}
	return 0;
}

function getPriceAfterDiscount(product: TProduct, isVatIncludedInPrice: boolean) {
	const price =
		isVatIncludedInPrice && product.vat
			? product.price + (product.price * CONFIG.VAT) / 100
			: product.price;

	if (product.discount?.type === "percent") {
		const dscountAmount = (price * product.discount.value) / 100;
		return product.price - dscountAmount;
	}
	if (product.discount?.type === "number") {
		const dscountAmount = price - product.discount.value;
		return dscountAmount;
	}
	return price;
}

// main
export function getCartCost({
	cart: rawCart,
	discounts,
	deliveryPrice = 0,
	freeDeliveryPrice = 0,
	freeShipping = false,
	isVatIncludedInPrice = false,
}: {
	cart: TCart["items"];
	discounts: TDiscount[];
	deliveryPrice?: number;
	freeDeliveryPrice?: number;
	/**
	 * When true, the customer is exempt from delivery fees regardless of the
	 * store's free-delivery threshold (e.g. an organization flagged with
	 * `freeShipping`). Forces `deliveryPrice` to 0.
	 */
	freeShipping?: boolean;
	isVatIncludedInPrice?: boolean;
}) {
	// Normalise for picking: drop missing lines, replace substituted lines with
	// the substitute product/amount/price. This is a no-op for regular (live)
	// carts where no item has a status field.
	const cart = getFulfilledCartItems(rawCart);

	// Convert cart items to the format expected by the discount engine
	// const cartForEngine = cart.map((item) => ({
	// 	amount: item.amount,
	// 	product: {
	// 		id: item.product.id,
	// 		price: item.product.price,
	// 	},
	// }));

	// Apply discounts using the new discount engine
	// const discountResult = DiscountEngine.calculateDiscounts(cartForEngine, discounts);

	// Map the results back to the original format with additional product info
	const result = cart.map((item, index) => {
		// const engineItem = discountResult.items[index];
		return {
			amount: item.amount,
			product: { ...item.product },
			originalPrice: item.product.price,
			finalPrice: getPriceAfterDiscount(item.product, isVatIncludedInPrice),
			finalDiscount: calculateDiscount(item.product, isVatIncludedInPrice),
		};
	});

	// isVatIncludedInPrice = false

	const cartDetails = result.reduce(
		(acc, item) => {
			const { product, amount, finalPrice, finalDiscount } = item;

			// The line's VAT-inclusive charge — HYP's exact per-line arithmetic
			// `round2(amount × round2(unitInclVat))`, shared with the HYP charge
			// (buildFulfilledHeshDescItems) and the fiscal document, so
			// display === charge === invoice with no rounding drift.
			const lineInclVat = chargeLineInclVat(finalPrice, !!product.vat, amount, isVatIncludedInPrice);

			// VAT is the charge minus its net portion, both rounded the same
			// per-line way, so `net + vat === finalCost` holds exactly.
			if (product.vat) {
				const netUnit = isVatIncludedInPrice
					? round2(finalPrice / (1 + CONFIG.VAT / 100)) // price includes VAT → strip it
					: round2(finalPrice); // price excludes VAT → net is the price itself
				const lineNet = round2(netUnit * amount);
				acc.vat = round2(acc.vat + (lineInclVat - lineNet));
			}

			// Round finalPrice to prevent floating point errors from discount engine
			const roundedFinalPrice = Number(finalPrice.toFixed(2));

			acc.cost += amount * roundedFinalPrice;
			acc.discount += finalDiscount ? amount * finalDiscount : finalDiscount;
			acc.finalCost += lineInclVat;
			acc.productsCost += lineInclVat;

			// Round all accumulated values to prevent floating point errors
			acc.cost = Number(acc.cost.toFixed(2));
			acc.discount = Number(acc.discount.toFixed(2));
			acc.finalCost = Number(acc.finalCost.toFixed(2));
			acc.productsCost = Number(acc.productsCost.toFixed(2));

			return acc;
		},
		{
			discount: 0,
			cost: 0,
			finalCost: 0,
			vat: 0,
			productsCost: 0,
			deliveryPrice: deliveryPrice,
		},
	);

	if (
		freeShipping ||
		(cartDetails.deliveryPrice && cartDetails.productsCost >= freeDeliveryPrice)
	) {
		cartDetails.deliveryPrice = 0;
	} else {
		cartDetails.finalCost += cartDetails.deliveryPrice;
	}

	return { items: result, ...cartDetails };
}
